import { useMemo, useState, useCallback, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridFilterModel } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { MasterIndexApi, AddressNoteApi } from '@/api';
import { DATE_TIME_FORMAT } from '@/config';
import { withLoader, WithLoaderProps } from '@/hocs';
import {
  IAddressNote,
  IAddressNoteType,
  IMasterAddressPopulated,
} from '@/models';
import { MasterIndexDetailsContext } from '@/pages/MasterIndex/components/MasterIndexDetailsPage/MasterIndexDetailsPage';
import NotesDataGridWrapper, {
  getPlainTextFromDescription,
} from '@/pages/Notes/NotesDataGridWrapper';
import { ConfirmTypes, useToastStore } from '@/store';
import { formatPersonName } from '@/utils';

export const MasterAddressNotesView = withLoader(
  ({ showLoader, hideLoader }: WithLoaderProps) => {
    const { updateToast } = useToastStore();
    const { addressId } = useParams();
    const { refreshTabCounts } =
      useOutletContext<MasterIndexDetailsContext>() || {};

    const [columnSettings, setColumnSettings] = useState<any[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [addressNoteTypes, setAddressNoteTypes] = useState<
      IAddressNoteType[]
    >([]);
    const [addressDetails, setAddressDetails] =
      useState<IMasterAddressPopulated | null>(null);

    const getTypeInfo = useCallback(
      (typeId: any, depth = 0): { name: string; isCaution: boolean } => {
        if (typeId && typeof typeId === 'object' && 'name' in typeId) {
          return {
            name: typeId.name || 'Unknown',
            isCaution: typeId.isCaution || false,
          };
        }

        if (depth > 2) return { name: String(typeId), isCaution: false };

        const addressNoteType = addressNoteTypes.find(
          (type) => type._id === typeId,
        );
        if (!addressNoteType) return { name: String(typeId), isCaution: false };

        const isObjectId = /^[0-9a-f]{24}$/.test(addressNoteType.name);
        if (isObjectId) {
          return getTypeInfo(addressNoteType.name, depth + 1);
        }
        return {
          name: addressNoteType.name,
          isCaution: addressNoteType.isCaution,
        };
      },
      [addressNoteTypes],
    );

    const additionalColumns: GridColDef[] = useMemo<GridColDef[]>(
      () => [
        {
          field: 'type',
          headerName: 'Type',
          minWidth: 300,
          renderCell: (params) => {
            const typeInfo = getTypeInfo(params.row.type);
            return (
              <div className="flex items-center h-full">
                <Typography variant="body2">{typeInfo.name}</Typography>
                {typeInfo.isCaution && (
                  <Chip
                    icon={<WarningAmberIcon fontSize="small" />}
                    label="Caution"
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  />
                )}
              </div>
            );
          },
        },
        {
          field: 'comment',
          headerName: 'Comment',
          flex: 1,
          renderCell: (params) => {
            const comment: string = getPlainTextFromDescription(
              params.row?.comment,
            );
            const display =
              comment.length > 50 ? `${comment.slice(0, 50)}...` : comment;
            return (
              <div className="flex items-center h-full">
                <Tooltip title={comment.length > 50 ? comment : ''} arrow>
                  <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                    {display}
                  </Typography>
                </Tooltip>
              </div>
            );
          },
        },
        {
          field: 'createdAt',
          headerName: 'Date and time',
          minWidth: 180,
          valueGetter: (_params, row) =>
            row?.createdAt
              ? moment(row?.createdAt).format(DATE_TIME_FORMAT)
              : '-',
        },
        {
          field: 'createdBy',
          headerName: 'Added by',
          minWidth: 200,
          valueGetter: (_params, row) =>
            formatPersonName(row?.createdBy) || '-',
        },
        {
          field: 'relatedTo',
          headerName: 'Related to',
          minWidth: 200,
          valueGetter: () => addressDetails?.addressLabel ?? '-',
        },
      ],
      [getTypeInfo, addressDetails],
    );

    const fetchAddressNoteTypes = useCallback(async () => {
      try {
        const response = await AddressNoteApi.getAddressNoteTypes();
        setAddressNoteTypes(response.data);
      } catch (err: any) {
        updateToast({
          open: true,
          message: err?.message ?? 'Failed to load address note types',
        });
      }
    }, [updateToast]);

    const fetchAddressDetails = useCallback(async () => {
      if (!addressId) return;
      try {
        const response = await MasterIndexApi.getMasterAddress(addressId);
        setAddressDetails(response.data);
      } catch (err: any) {
        updateToast({
          open: true,
          message: err?.message ?? 'Failed to load address details',
        });
      }
    }, [addressId, updateToast]);

    const fetchAddressNotes = useCallback(
      async (
        page: number,
        limit: number,
        filter: GridFilterModel | string,
      ): Promise<{ rows: IAddressNote[]; totalCount: number }> => {
        try {
          const filterObj =
            typeof filter === 'string' && filter
              ? JSON.parse(filter)
              : (filter as GridFilterModel) || {
                  items: [],
                  quickFilterValues: [],
                };

          const params = {
            page: page || 1,
            limit: limit || 100,
            filter: JSON.stringify({
              items: [
                {
                  field: 'address',
                  operator: 'equals',
                  value: addressId,
                },
                ...(filterObj.items || []),
              ],
              logicOperator: 'and',
              quickFilterValues: filterObj.quickFilterValues || [],
              quickFilterLogicOperator: 'and',
            }),
            sort: JSON.stringify({ createdAt: -1 }),
          };

          const response = await AddressNoteApi.getPaginatedAddressNotes(
            params,
          );
          const { data } = response;
          return {
            rows: data.docs,
            totalCount: data.totalDocs,
          };
        } catch (err: any) {
          updateToast({ open: true, message: err.message });
          return { rows: [], totalCount: 0 };
        }
      },
      [addressId, updateToast],
    );

    const fetchNoteDetails = useCallback(
      async (noteId: string) => {
        try {
          const response = await AddressNoteApi.getAddressNote(noteId);
          return response.data;
        } catch (err: any) {
          updateToast({
            open: true,
            message: err?.message ?? 'Failed to load note details',
          });
          throw err;
        }
      },
      [updateToast],
    );

    const onSaveNote = useCallback(
      async (payload: any) => {
        if (!addressId) return;

        const noteId: string | undefined = payload?._id;

        showLoader();
        try {
          if (noteId) {
            await AddressNoteApi.updateAddressNote(noteId, {
              comment: payload.comment,
              type: payload.type,
              address: addressId || '',
            });
            updateToast({
              type: ConfirmTypes.SUCCESS,
              open: true,
              message: 'Note updated successfully',
            });
          } else {
            await AddressNoteApi.createAddressNote({
              comment: payload.comment,
              type: payload.type,
              address: addressId || '',
            });
            updateToast({
              type: ConfirmTypes.SUCCESS,
              open: true,
              message: 'Note created successfully',
            });
          }
        } catch (err: any) {
          updateToast({
            open: true,
            message: err?.message ?? 'Failed to save note',
          });
        } finally {
          setRefreshKey((prevState) => prevState + 1);
          refreshTabCounts?.();
          hideLoader();
        }
      },
      [addressId, showLoader, hideLoader, updateToast, refreshTabCounts],
    );

    const onDeleteNote = useCallback(
      async (noteId: string) => {
        if (!addressId || !noteId) return;
        showLoader();
        try {
          await AddressNoteApi.deleteAddressNote(noteId);
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: 'Note deleted successfully',
          });
        } catch (err: any) {
          updateToast({
            open: true,
            message: err?.message ?? 'Failed to delete note',
          });
        } finally {
          setRefreshKey((prevState) => prevState + 1);
          refreshTabCounts?.();
          hideLoader();
        }
      },
      [addressId, showLoader, hideLoader, updateToast, refreshTabCounts],
    );

    useEffect(() => {
      if (addressId) {
        fetchAddressNoteTypes();
        fetchAddressDetails();
      }
    }, [addressId, fetchAddressNoteTypes, fetchAddressDetails]);

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        <NotesDataGridWrapper
          name="master-address-notes"
          fetchData={fetchAddressNotes}
          columnSettings={columnSettings}
          setColumnSettings={setColumnSettings}
          additionalColumns={additionalColumns}
          onSaveNote={onSaveNote}
          onDeleteNote={onDeleteNote}
          showTypeSelect={true}
          noteTypes={addressNoteTypes}
          fetchNoteDetails={fetchNoteDetails}
          hideQuickFilter={false}
          hideFilter={false}
          addressRelatedTo={addressDetails?.addressLabel}
          key={refreshKey}
        />
      </Box>
    );
  },
);

export default MasterAddressNotesView;
